import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const GROESSEN = [
  { id: '62x40', label: '62 × 40 mm', width: '62mm', height: '40mm' },
  { id: '50x30', label: '50 × 30 mm', width: '50mm', height: '30mm' },
  { id: '40x30', label: '40 × 30 mm', width: '40mm', height: '30mm' },
];

// Physical font sizes (pt = 1/72 inch, reliable for print)
const FONTS = {
  '62x40': { name: '9pt',  standort: '7pt',  kennung: '6pt'  },
  '50x30': { name: '8pt',  standort: '6pt',  kennung: '5.5pt' },
  '40x30': { name: '7pt',  standort: '5.5pt', kennung: '5pt'  },
};

export default function EtikettPage() {
  const { id } = useParams();
  const [behaelter, setBehaelter] = useState(null);
  const [fehler, setFehler] = useState(false);
  const [groesse, setGroesse] = useState('62x40');

  const g = GROESSEN.find(s => s.id === groesse) || GROESSEN[0];
  const f = FONTS[g.id];

  useEffect(() => {
    api.get(`/behaelter/${id}`)
      .then(r => setBehaelter(r.data))
      .catch(() => setFehler(true));
  }, [id]);

  // Inject @page into <head> for maximum browser compatibility
  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'etikett-page-size';
    el.textContent = `@page { size: ${g.width} ${g.height}; margin: 0mm; }`;
    document.head.appendChild(el);
    return () => { document.getElementById('etikett-page-size')?.remove(); };
  }, [g.width, g.height]);

  if (fehler) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
        <div style={{ fontSize: '48px' }}>⚠️</div>
        <p>Kiste nicht gefunden.</p>
        <a href="/behaelter" style={{ color: '#6366f1' }}>← Zur Kistenübersicht</a>
      </div>
    );
  }

  if (!behaelter) {
    return <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Etikett wird geladen…</div>;
  }

  const kennung = `K-${String(behaelter.id).padStart(4, '0')}`;
  // QR side: square = label height minus 4mm padding (2mm top + 2mm bottom)
  const qrSize = `calc(${g.height} - 4mm)`;

  return (
    <>
      <style>{`
        /* ── Base ─────────────────────────────────────────────── */
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; padding: 0; background: #f1f5f9; }

        /* ── Screen controls (hidden in print) ────────────────── */
        .etikett-ui {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 10px 14px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        /* ── Screen preview wrapper ───────────────────────────── */
        .etikett-preview {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 32px 16px 24px;
          /* no min-height: 100vh here — avoids extra print page */
        }

        /* ── Screen hint (hidden in print) ───────────────────── */
        .etikett-hint {
          font-size: 11px;
          color: #94a3b8;
          margin-top: 12px;
          text-align: center;
          line-height: 1.5;
        }

        /* ── The actual label ─────────────────────────────────── */
        .etikett {
          background: white;
          width: ${g.width};
          height: ${g.height};
          display: flex;
          flex-direction: row;
          align-items: stretch;
          overflow: hidden;
          font-family: Arial, Helvetica, sans-serif;
          /* screen-only decoration */
          box-shadow: 0 4px 20px rgba(0,0,0,0.18);
          border-radius: 3px;
        }

        /* ── QR column ─────────────────────────────────────────── */
        .etikett-qr {
          flex: 0 0 auto;
          width: ${qrSize};
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2mm;
        }

        .etikett-qr img {
          width: calc(${g.height} - 8mm);
          height: calc(${g.height} - 8mm);
          display: block;
          image-rendering: pixelated;
          image-rendering: crisp-edges;
        }

        /* ── Text column ───────────────────────────────────────── */
        .etikett-text {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 2mm 2mm 2mm 0;
          overflow: hidden;
        }

        .etikett-name {
          font-size: ${f.name};
          font-weight: 700;
          color: #000;
          line-height: 1.15;
          word-break: break-word;
          overflow-wrap: break-word;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          margin-bottom: 1mm;
        }

        .etikett-standort {
          font-size: ${f.standort};
          color: #333;
          line-height: 1.2;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 1mm;
        }

        .etikett-kennung {
          font-size: ${f.kennung};
          color: #666;
          font-family: 'Courier New', monospace;
          letter-spacing: 0.3px;
        }

        /* ── PRINT ─────────────────────────────────────────────── */
        @media print {
          /* 1. Hide all screen-only chrome */
          .etikett-ui    { display: none !important; }
          .etikett-hint  { display: none !important; }

          /* 2. Collapse html/body/root to label size */
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: ${g.width} !important;
            height: ${g.height} !important;
            overflow: hidden !important;
            background: white !important;
          }

          #root {
            margin: 0 !important;
            padding: 0 !important;
            width: ${g.width} !important;
            height: ${g.height} !important;
            overflow: hidden !important;
          }

          /* 3. Preview wrapper becomes the label container */
          .etikett-preview {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            width: ${g.width} !important;
            height: ${g.height} !important;
            overflow: hidden !important;
            background: white !important;
          }

          /* 4. Label itself: remove screen decoration */
          .etikett {
            box-shadow: none !important;
            border-radius: 0 !important;
            border: none !important;
            width: ${g.width} !important;
            height: ${g.height} !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* 5. @page rule also here as fallback */
          @page {
            size: ${g.width} ${g.height};
            margin: 0mm;
          }
        }
      `}</style>

      {/* ── Screen controls (hidden in print) ─── */}
      <div className="etikett-ui">
        <a href={`/behaelter/${id}`}
          style={{ color: '#64748b', textDecoration: 'none', fontSize: '13px', flexShrink: 0 }}>
          ← Zurück
        </a>
        <span style={{ fontSize: '12px', color: '#94a3b8' }}>|</span>
        <span style={{ fontSize: '12px', color: '#374151', flexShrink: 0 }}>Größe:</span>
        {GROESSEN.map(s => (
          <button key={s.id} onClick={() => setGroesse(s.id)}
            style={{
              padding: '5px 10px',
              background: groesse === s.id ? '#f59e0b' : '#f1f5f9',
              color: groesse === s.id ? 'white' : '#374151',
              border: 'none', borderRadius: '6px',
              cursor: 'pointer', fontSize: '12px',
              fontWeight: groesse === s.id ? '700' : '400',
            }}>
            {s.label}
          </button>
        ))}
        <button onClick={() => window.print()}
          style={{ marginLeft: 'auto', padding: '8px 18px', background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }}>
          🖨️ Etikett drucken
        </button>
      </div>

      {/* ── Preview + print area ─────────────── */}
      <div className="etikett-preview">

        {/* THE LABEL — this is what gets printed */}
        <div className="etikett">
          <div className="etikett-qr">
            {behaelter.qr_code
              ? <img src={behaelter.qr_code} alt={`QR-Code für ${behaelter.name}`} />
              : <div style={{ width: `calc(${g.height} - 8mm)`, height: `calc(${g.height} - 8mm)`, background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', color: '#9ca3af' }}>QR fehlt</div>
            }
          </div>
          <div className="etikett-text">
            <div className="etikett-name">{behaelter.name}</div>
            {(behaelter.standort_icon || behaelter.standort_name) && (
              <div className="etikett-standort">
                {behaelter.standort_icon} {behaelter.standort_name || ''}
              </div>
            )}
            <div className="etikett-kennung">{kennung}</div>
          </div>
        </div>

        {/* Screen-only hint — hidden in print via CSS */}
        <p className="etikett-hint">
          Vorschau: {g.label} · Im Druckdialog Papierformat auf Etikettenformat setzen &amp; Ränder auf „Keine"
        </p>
      </div>
    </>
  );
}
